// Countries of the billing profile (ISO 3166-1 alpha-2), named in the current language by the browser's Intl, and the
// sets the VAT rules of sculpt-api use (sculpt/billing/vat.py): where a SIREN is required, and the other member states
// whose customers' orders wait while VIES cannot check their VAT number.

const CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD ' +
  'CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG ' +
  'GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR ' +
  'KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF ' +
  'NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM ' +
  'SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK ' +
  'YE YT ZA ZM ZW'
).split(' ');

/** France and its overseas departments: every business there has a SIREN, which e-invoicing addresses. */
export const SIREN_COUNTRIES = new Set(['FR', 'GP', 'MQ', 'RE', 'GF', 'YT']);

/** The other member states: a VAT number VIES confirms is reverse-charged; one it cannot check holds the orders. */
const OTHER_EU = new Set(['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE']);

export function needsSiren(country: string): boolean {
  return SIREN_COUNTRIES.has(country);
}

export function inOtherMemberState(country: string | null): boolean {
  return country !== null && OTHER_EU.has(country);
}

/** The countries as [code, name], sorted by name in `locale`. */
export function countryOptions(locale: string): [string, string][] {
  const lang = locale === 'fr' ? 'fr' : 'en';
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([lang], { type: 'region' });
  } catch {
    names = null;
  }
  return CODES.map((code): [string, string] => [code, names?.of(code) ?? code]).sort((a, b) => a[1].localeCompare(b[1], lang));
}

/** A SIRET pasted where the SIREN goes: its first nine digits are the SIREN (sculpt-api `profile._clean` does the same). */
export function sirenOfSiret(value: string): string {
  const digits = value.replace(/\s/g, '');
  return /^\d{14}$/.test(digits) ? digits.slice(0, 9) : value;
}

/** "9 digits, spaces allowed" (sculpt-api `vat.is_siren` after removing the spaces). */
export function isSiren(value: string): boolean {
  return /^\d{9}$/.test(value.replace(/\s/g, ''));
}
